import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, Spinner, toast } from "@heroui/react";
import { useCallback, useEffect } from "react";
import type { BlacklistItem } from "@repo/api/blacklist.type";
import { useAuth } from "@repo/context/auth/auth";
import { useTemplateProtectionGuidelines } from "@repo/context/template-protection-guidelines";
import { BlacklistSetListItem } from "@repo/ui/pages/template_service/blacklist/BlacklistSetListItem";
import { formatAddedAt } from "@repo/ui/pages/template_service/blacklist/utils";
import { InfiniteScroll } from "@/components/infinite-scroll";
import {
  usePublicBlacklistsInfinite,
  useSubscribeBlacklist,
  useUnsubscribeBlacklist,
} from "@repo/ui/pages/template_service/blacklist/use-blacklist";

export const Route = createFileRoute("/template_protections/blacklist/public/")({
  component: PublicBlacklistPage,
});

const PAGE_SIZE = 20;

export default function PublicBlacklistPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { hasAgreedToGuidelines } = useTemplateProtectionGuidelines();
  const enabled = isAuthenticated === true;
  const {
    data,
    error,
    isPending,
    hasNextPage,
    isFetchingNextPage: loadingMore,
    fetchNextPage,
  } = usePublicBlacklistsInfinite(PAGE_SIZE, enabled);
  const list: BlacklistItem[] = data?.pages.flatMap((p) => p.list) ?? [];
  // 未登录时不请求，isPending 会一直为 true，这里按非加载态处理
  const loading = enabled && isPending;
  const hasMore = hasNextPage ?? false;

  useEffect(() => {
    if (error) {
      toast.danger((error as { msg?: string }).msg || "加载失败");
    }
  }, [error]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchNextPage();
  }, [loading, loadingMore, hasMore, fetchNextPage]);

  // 订阅 / 取消成功后由缓存失效自动刷新整个列表（含 is_subscribed）
  const subscribe = useSubscribeBlacklist();
  const unsubscribe = useUnsubscribeBlacklist();
  const actionId = subscribe.isPending
    ? subscribe.variables
    : unsubscribe.isPending
      ? unsubscribe.variables
      : null;

  const handleSubscribe = (item: BlacklistItem) => subscribe.mutate(item.id);
  const handleUnsubscribe = (item: BlacklistItem) => unsubscribe.mutate(item.id);

  return (
    <div className="px-4 space-y-3">
      <InfiniteScroll
        loading={loadingMore || loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingText="加载中"
        noMoreText="没有更多了"
      >
        {list.length === 0 && !loading ? (
          <div className="py-10 text-center text-gray-500 text-sm">
            暂无公共黑名单
          </div>
        ) : (
          list.map((item) => (
            <BlacklistSetListItem
              key={item.id}
              item={item}
              dateLabel={`最后更新 ${formatAddedAt(item.updated_at)}`}
              onPress={() =>
                navigate({
                  to: "/template_protections/blacklist/public/$id",
                  params: { id: item.id },
                })
              }
              onOwnerPress={() =>
                navigate({
                  to: "/template_protections/trusted-query",
                  search: { search: item.uid },
                })
              }
              actions={
                item.is_subscribed ? (
                  <Button
                    size="sm"
                    variant="danger-soft"
                    className="rounded-full"
                    isDisabled={!hasAgreedToGuidelines}
                    isPending={actionId === item.id}
                    onPress={() => handleUnsubscribe(item)}
                  >
                    {({ isPending }) => (
                      <>
                        {isPending && <Spinner size="sm" color="current" />}
                        取消订阅
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="tertiary"
                    className="rounded-full"
                    isDisabled={!hasAgreedToGuidelines}
                    isPending={actionId === item.id}
                    onPress={() => handleSubscribe(item)}
                  >
                    {({ isPending }) => (
                      <>
                        {isPending && <Spinner size="sm" color="current" />}
                        订阅
                      </>
                    )}
                  </Button>
                )
              }
            />
          ))
        )}
      </InfiniteScroll>
      <p className="text-xs text-gray-500 pb-4 text-center">
        公共黑名单为共享者自行维护，兽聚汇官方不对其内容负责。
      </p>
    </div>
  );
}
